import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";

export class ImageCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Source URL patterns
    this.commandPatterns.set(
      /(?:set |change )?(?:image |source |src |url )(?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { src: match[1] } })
    );

    // Alt text patterns
    this.commandPatterns.set(
      /(?:set |change )?(?:alt |alternative )(?:text )?(?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { alt: match[1] } })
    );

    // Object fit patterns
    this.commandPatterns.set(
      /(?:set |make )?(?:object[\s-]?fit |fit )(?:to )?(cover|contain|fill|none|scale-down)/i,
      (match) => ({ properties: { objectFit: match[1].toLowerCase() } })
    );

    // Aspect ratio patterns
    this.commandPatterns.set(
      /(?:set )?aspect[\s-]?ratio (?:to )?(\d+):(\d+)/i,
      (match) => ({
        properties: {
          aspectRatio: `${match[1]}/${match[2]}`
        }
      })
    );

    this.commandPatterns.set(
      /(?:make |set )?(square|landscape|portrait|wide|ultrawide)/i,
      (match) => {
        const ratioMap: Record<string, string> = {
          square: "1/1",
          landscape: "16/9",
          portrait: "9/16",
          wide: "21/9",
          ultrawide: "32/9"
        };
        const ratio = match[1].toLowerCase();
        return { properties: { aspectRatio: ratioMap[ratio] } };
      }
    );

    // Border radius patterns
    this.commandPatterns.set(
      /(?:make |set )?(?:image )?(rounded|circular|circle)/i,
      (match) => {
        const style = match[1].toLowerCase();
        const radius = style === "circular" || style === "circle" ? "50%" : "8px";
        return { style: { borderRadius: radius } };
      }
    );

    // Lazy loading patterns
    this.commandPatterns.set(
      /(enable|disable) lazy[\s-]?load(?:ing)?/i,
      (match) => ({ properties: { loading: match[1].toLowerCase() === "enable" ? "lazy" : "eager" } })
    );

    // Caption patterns
    this.commandPatterns.set(
      /(?:add |set )?caption (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { caption: match[1] } })
    );

    this.commandPatterns.set(
      /remove caption/i,
      () => ({ properties: { caption: null } })
    );

    // Border patterns
    this.commandPatterns.set(
      /(?:add |set )?border (?:width )?(\d+)(?:px)?/i,
      (match) => ({ style: { borderWidth: `${match[1]}px`, borderStyle: "solid" } })
    );

    this.commandPatterns.set(
      /remove border/i,
      () => ({ style: { borderWidth: "0", borderStyle: "none" } })
    );

    // Shadow patterns
    this.commandPatterns.set(
      /(?:add |set )?(shadow|drop[\s-]?shadow)/i,
      () => ({ style: { boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)" } })
    );

    this.commandPatterns.set(
      /remove shadow/i,
      () => ({ style: { boxShadow: "none" } })
    );

    // Opacity patterns
    this.commandPatterns.set(
      /(?:set )?opacity (?:to )?(\d+)%?/i,
      (match) => {
        const opacity = parseInt(match[1]) / (match[1].includes('%') ? 100 : 1);
        return { style: { opacity: Math.min(1, Math.max(0, opacity)).toString() } };
      }
    );

    // Grayscale/filter patterns
    this.commandPatterns.set(
      /(?:make |apply )?(grayscale|sepia|blur|brightness|contrast)/i,
      (match) => {
        const filterMap: Record<string, string> = {
          grayscale: "grayscale(100%)",
          sepia: "sepia(100%)",
          blur: "blur(4px)",
          brightness: "brightness(150%)",
          contrast: "contrast(150%)"
        };
        return { style: { filter: filterMap[match[1].toLowerCase()] } };
      }
    );

    this.commandPatterns.set(
      /remove filter/i,
      () => ({ style: { filter: "none" } })
    );
  }

  canHandle(command: string, component: Component): boolean {
    return component.type === ComponentType.IMAGE && this.hasImageKeywords(command);
  }

  private hasImageKeywords(command: string): boolean {
    const imageKeywords = [
      "image", "img", "picture", "photo",
      "source", "src", "url", "link",
      "alt", "caption", "description",
      "fit", "cover", "contain", "fill",
      "aspect", "ratio", "square", "landscape", "portrait",
      "rounded", "circular", "circle",
      "border", "shadow", "opacity",
      "grayscale", "sepia", "blur", "filter",
      "lazy", "loading"
    ];
    const lowerCommand = command.toLowerCase();
    return imageKeywords.some(keyword => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    // Split complex commands
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {}, style: {} };

    for (const part of commandParts) {
      // Try pattern matching
      const patternMatch = this.matchPatterns(part, component);
      if (patternMatch) {
        if (patternMatch.properties) {
          changes.properties = { ...changes.properties, ...patternMatch.properties };
        }
        if (patternMatch.style) {
          changes.style = { ...changes.style, ...patternMatch.style };
        }
      }

      // Parse colors for border
      const colorValue = this.parseColorValue(part);
      if (colorValue && part.toLowerCase().includes("border")) {
        changes.style = { ...changes.style, borderColor: colorValue };
      }

      // Parse size values
      const lowerPart = part.toLowerCase();
      if (lowerPart.includes("width") && !lowerPart.includes("border")) {
        const widthValue = this.parseNumericValue(part);
        if (widthValue) {
          changes.size = { ...changes.size, width: widthValue };
        }
      }

      if (lowerPart.includes("height")) {
        const heightValue = this.parseNumericValue(part);
        if (heightValue) {
          changes.size = { ...changes.size, height: heightValue };
        }
      }

      // Parse margin and padding
      if (lowerPart.includes("margin")) {
        const marginValue = this.parseNumericValue(part);
        if (marginValue) {
          changes.style = { ...changes.style, margin: marginValue };
        }
      }

      if (lowerPart.includes("padding")) {
        const paddingValue = this.parseNumericValue(part);
        if (paddingValue) {
          changes.style = { ...changes.style, padding: paddingValue };
        }
      }
    }

    // Check if we found any changes
    if (Object.keys(changes.properties || {}).length > 0 ||
        Object.keys(changes.style || {}).length > 0 ||
        changes.size) {

      const validation = this.validateChanges(changes, component);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error,
          suggestions: await this.getSuggestions(component)
        };
      }

      return {
        success: true,
        command: {
          type: this.determineCommandType(changes),
          changes,
          confidence: this.calculateConfidence(command, changes),
          description: this.generateDescription(changes, component)
        },
        suggestions: await this.getSuggestions(component)
      };
    }

    return {
      success: false,
      error: "Could not understand the image command",
      suggestions: await this.getSuggestions(component)
    };
  }

  async getExampleCommands(): Promise<string[]> {
    return [
      'Set source to "https://example.com/image.jpg"',
      'Change image URL to "path/to/image.png"',
      'Set alt text to "Product photo"',
      'Make image cover',
      'Set object fit to contain',
      'Set aspect ratio to 16:9',
      'Make square image',
      'Make landscape orientation',
      'Make image rounded',
      'Make circular image',
      'Add caption "Beautiful sunset"',
      'Add border 2px',
      'Add shadow effect',
      'Set opacity to 80%',
      'Apply grayscale filter',
      'Make image blur',
      'Enable lazy loading',
      'Set width to 300px',
      'Set height to 200px',
      'Remove border and add shadow'
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];

    // Source suggestions
    if (!component.properties?.src) {
      suggestions.push('Set image source URL', 'Add image link');
    } else {
      suggestions.push('Change image source', 'Update image URL');
    }

    // Alt text suggestions
    if (!component.properties?.alt) {
      suggestions.push('Add alt text for accessibility', 'Set alternative text');
    } else {
      suggestions.push('Update alt text');
    }

    // Object fit suggestions
    const currentFit = component.properties?.objectFit || "fill";
    if (currentFit !== "cover") {
      suggestions.push("Make image cover");
    }
    if (currentFit !== "contain") {
      suggestions.push("Set object fit to contain");
    }

    // Aspect ratio suggestions
    if (!component.properties?.aspectRatio) {
      suggestions.push("Make square aspect ratio", "Set aspect ratio to 16:9");
    }

    // Caption suggestions
    if (!component.properties?.caption) {
      suggestions.push('Add image caption');
    } else {
      suggestions.push('Update caption', 'Remove caption');
    }

    // Style suggestions
    if (!component.style?.borderRadius) {
      suggestions.push("Make rounded corners", "Make circular image");
    }

    if (!component.style?.boxShadow || component.style.boxShadow === "none") {
      suggestions.push("Add shadow effect");
    } else {
      suggestions.push("Remove shadow");
    }

    if (!component.style?.filter || component.style.filter === "none") {
      suggestions.push("Apply grayscale filter", "Add blur effect");
    } else {
      suggestions.push("Remove filter");
    }

    // Loading suggestions
    if (component.properties?.loading !== "lazy") {
      suggestions.push("Enable lazy loading");
    }

    return suggestions.slice(0, 10);
  }

  protected override validateChanges(changes: PropertyChange, component: Component): { valid: boolean; error?: string } {
    // Validate object fit
    if (changes.properties?.objectFit) {
      const validFits = ["cover", "contain", "fill", "none", "scale-down"];
      if (!validFits.includes(changes.properties.objectFit)) {
        return { valid: false, error: `Invalid object fit: ${changes.properties.objectFit}` };
      }
    }

    // Validate loading
    if (changes.properties?.loading) {
      const validLoading = ["lazy", "eager"];
      if (!validLoading.includes(changes.properties.loading)) {
        return { valid: false, error: `Invalid loading value: ${changes.properties.loading}` };
      }
    }

    // Validate aspect ratio format
    if (changes.properties?.aspectRatio) {
      const ratioPattern = /^\d+\/\d+$/;
      if (!ratioPattern.test(changes.properties.aspectRatio)) {
        return { valid: false, error: `Invalid aspect ratio format: ${changes.properties.aspectRatio}` };
      }
    }

    // Validate opacity
    if (changes.style?.opacity) {
      const opacity = parseFloat(changes.style.opacity);
      if (isNaN(opacity) || opacity < 0 || opacity > 1) {
        return { valid: false, error: `Invalid opacity value: ${changes.style.opacity}` };
      }
    }

    return super.validateChanges(changes, component);
  }
}

export const imageCommandHandler = new ImageCommandHandler();
